import { Injectable } from '@angular/core';

import {
  asapScheduler,
  defaultIfEmpty,
  filter,
  finalize,
  from,
  map,
  mergeMap,
  Observable,
  observeOn,
  of,
  take,
  tap,
  BehaviorSubject,
} from 'rxjs';

import FeatureLayer from '@arcgis/core/layers/FeatureLayer';
import SpatialReference from '@arcgis/core/geometry/SpatialReference';

import { AppConfigService } from './app-config.service';
import { CacheFactoryService, ICacheService } from './cache-factory.service';
import { EsriUtils } from '../utils/esri.utils';
import { ToastService } from './toast.service';
import { WkIdType } from '../constants/wkid.constant';
import { TranslationService } from './translation.service';

@Injectable()
export class PortugalWkidService {
  private cacheService: ICacheService;

  public wkidDefault = 4326;

  private _wkidSubject = new BehaviorSubject<WkIdType>(this.wkidDefault);

  private _wgs84ModeSubject = new BehaviorSubject<boolean>(false);

  get wgs84Mode() {
    return this._wgs84ModeSubject.getValue();
  }

  get onWgs84ModeToggle() {
    return this._wgs84ModeSubject.asObservable();
  }

  get onWkidChange() {
    return this._wkidSubject.asObservable();
  }

  constructor(
    private appConfig: AppConfigService,
    private toastService: ToastService,
    cacheFactory: CacheFactoryService,
    private ts: TranslationService
  ) {
    this.cacheService = cacheFactory.getOrCreate('map-domains');
  }

  setWgs84Mode() {
    this._wgs84ModeSubject.next(!this.wgs84Mode);
  }

  getAreaGeomList(): Observable<{ wkid: number; geometry: __esri.Geometry }[]> {
    return this.cacheService.getCached({
      path: `portugalWkidFeatureList`,
      lifetimeSeconds: 0,
      source: () => {
        const featureLayer = new FeatureLayer({
          url: this.appConfig.normalizeUrl('~portugalWkid'),
        });

        const query = featureLayer.createQuery();
        query.where = '1=1 AND WKID <> ' + this.wkidDefault;
        query.outSpatialReference = new SpatialReference({ wkid: 102100 });
        query.outFields = ['wkid'];
        query.returnGeometry = true;

        return EsriUtils.queryFeatures(featureLayer, query).pipe(
          finalize(() => {
            featureLayer.destroy();
          }),
          map(({ features }) => {
            return features.map((feature) => ({
              wkid: feature.attributes.wkid,
              geometry: feature.geometry,
            }));
          }),
          tap({
            error: () => {
              this.toastService.error(
                this.ts.getCurrentLanguage()['failed-load-WKID-list']
              );
            },
          })
        );
      },
    });
  }

  projectGeomToAreaWkid<T extends __esri.Geometry>(geom: T): Observable<T> {
    if (!geom) {
      return of(null);
    }

    return this.getAreaGeomList().pipe(
      mergeMap((list) => {
        if (!list?.length) {
          return of(geom);
        }

        return from(list).pipe(
          filter((item) => EsriUtils.intersects(item.geometry, geom)),
          take(1),
          map((item) => item.wkid),
          defaultIfEmpty(this.wkidDefault),
          observeOn(asapScheduler),
          map((wkid) => {
            this._wkidSubject.next(wkid);
            const geomProjected = EsriUtils.project(geom, { wkid });
            return geomProjected;
          })
        );
      }),
      observeOn(asapScheduler)
    );
  }
}
